
async function safeFetchJson(url, { timeoutMs = 3500 } = {}) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}


function isStorageAvailable(type) {
  try {
    const s = window[type];
    if (!s) return false;
    const k = '__hb_diag__';
    s.setItem(k, '1');
    s.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

async function getDiagnostics(cart) {
  try {
    // 1) Network 
    const network = await safeFetchJson('https://ipapi.co/json/', { timeoutMs: 3500 });

    // 2) Navigator 
    const nav = window.navigator;
    const navigatorInfo = {
      userAgent: nav.userAgent,
      platform: nav.platform,
      language: nav.language,
      languages: nav.languages,
      onLine: nav.onLine,
      hardwareConcurrency: nav.hardwareConcurrency,
      deviceMemory: nav.deviceMemory,
      appVersion: nav.appVersion,
      appName: nav.appName,
      appCodeName: nav.appCodeName,
      connection: nav.connection ? {
        effectiveType: nav.connection.effectiveType,
        downlink: nav.connection.downlink,
        rtt: nav.connection.rtt,
        saveData: nav.connection.saveData,
      } : null,
      clipboard: !!nav.clipboard,
      bluetooth: !!nav.bluetooth,
    };

    // 3) Screen 
    const scr = window.screen;
    const screenInfo = {
      width: scr?.width ?? null,
      height: scr?.height ?? null,
      availWidth: scr?.availWidth ?? null,
      availHeight: scr?.availHeight ?? null,
      colorDepth: scr?.colorDepth ?? null,
      pixelDepth: scr?.pixelDepth ?? null,
      devicePixelRatio: window.devicePixelRatio ?? null,
      orientation: scr?.orientation
        ? { type: scr.orientation.type, angle: scr.orientation.angle }
        : null,
      innerWidth: window.innerWidth ?? null,
      innerHeight: window.innerHeight ?? null,
      outerWidth: window.outerWidth ?? null,
      outerHeight: window.outerHeight ?? null,
    };


    // meta 
    const meta = {
      timestamp: new Date().toISOString(),
      timezone: (Intl.DateTimeFormat().resolvedOptions().timeZone) || null,
      pageUrl: window.location.href,
      referrer: document.referrer || null,
      // userAgentData 
      userAgentData: (nav.userAgentData && typeof nav.userAgentData.toJSON === 'function')
        ? nav.userAgentData.toJSON()
        : null,
    };

    
    const storage = {
      cookiesEnabled: !!nav.cookieEnabled,
      localStorage: isStorageAvailable('localStorage'),
      sessionStorage: isStorageAvailable('sessionStorage'),
    };

    
    const runtime = {
      performanceNow: (typeof performance?.now === 'function') ? performance.now() : null,
      memory: performance?.memory
        ? {
            jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
            totalJSHeapSize: performance.memory.totalJSHeapSize,
            usedJSHeapSize: performance.memory.usedJSHeapSize,
          }
        : null,
    };


    const documentInfo = {
      title: document.title,
      visibilityState: document.visibilityState, // "visible" / "hidden" / "prerender" etc.
      hasFocus: typeof document.hasFocus === 'function' ? document.hasFocus() : null,
      readyState: document.readyState, // "loading" / "interactive" / "complete"
    };

    const diagnostics = {
      network,                
      cart: cart ?? null,
      theme: globalThis.Shopify?.theme ?? null,
      cust: hvgUserId ?? null,
      navigator: navigatorInfo,
      screen: screenInfo,
      meta,
      storage,
      runtime,
      document: documentInfo,
    };

    return diagnostics;
  } catch {
    return {
      network: null,
      cart: cart ?? null,
      theme: globalThis.Shopify?.theme ?? null,
      cust: hvgUserId ?? null,
      navigator: null,
      screen: null,
      meta: { timestamp: new Date().toISOString() },
      storage: null,
      runtime: null,
      document: null,
    };
  }
}



const HVG_DIAG_ENDPOINT = 'https://enroute.hiveberg.com/trap/record';


function randomKey12() {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}


function loadHvgDiagnostics() {
  try {
    const raw = localStorage.getItem('hvgDiagnostic');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === 'object') return [parsed];
    return [];
  } catch {
    return [];
  }
}


function saveHvgDiagnostics(entries) {
  try {
    localStorage.setItem('hvgDiagnostic', JSON.stringify(entries));
    return true;
  } catch {
    return false;
  }
}


async function postWithTimeout(url, body, { timeoutMs = 3000 } = {}) {

  const payload = {
    key:body?.ids?.key,
    category: body?.ids?.category,
    name: body?.ids?.name,
    content: JSON.stringify(body)
  }

  const ctrl = new AbortController();
  const to = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      method: 'POST',
      signal: ctrl.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(to);
  }
}

async function recordDiagnostic(category, name, key) {
  try {
    const finalKey = (key == null) ? randomKey12() : key;
    const entries = loadHvgDiagnostics();
    if (entries.some(e => e?.category === category && e?.name === name && e?.key === finalKey)) {
      return;
    }
    const ids = { category, name, key: finalKey, ts: new Date().toISOString() };
    let cart = null;
    try {
      const appEl = document.querySelector('#app');
      const vueRef =
        appEl?.__vue__ ||
        appEl?._vnode?.ctx ||
        appEl?.__vue_app__?.config?.globalProperties ||
        null;
      cart = vueRef?.cart ?? null;
    } catch {
      cart = null;
    }
    let payload;
    try {
      const diag = await getDiagnostics(cart);
      payload= diag;
      diag.ids = ids;
    } catch {
      payload = { ids };
    }
    try {
      await postWithTimeout(HVG_DIAG_ENDPOINT, payload);
    } catch {}
    try {
      entries.push({ category, name, key: finalKey });
      saveHvgDiagnostics(entries);
    } catch {}
  } catch {}
}


function initDiagnosticFromQuery() {
  window.addEventListener(
    'load',
    () => {
      setTimeout(() => {
        try {
          const qs = new URLSearchParams(window.location.search);
          const hasAny =
            qs.has('diagcategory') || qs.has('diagname') || qs.has('diagkey');
          if (!hasAny) return;
          const category = qs.get('diagcategory') || null;
          const name = qs.get('diagname') || null;
          const key = qs.get('diagkey') || null;
          void recordDiagnostic(category, name, key);
        } catch {

        }
      }, 3000); 
    },
    { once: true, passive: true }
  );
}

initDiagnosticFromQuery();